import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLog, type AuditLogRow } from '@/api/audit';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const ENTITY_LABEL: Record<string, string> = {
  'acc.accounts': 'Рахунок',
  'doc.goods_sales': 'Реалізація',
};

const ACTION_LABEL: Record<string, string> = {
  INSERT: 'Створено',
  UPDATE: 'Оновлено',
  DELETE: 'Видалено',
  POST: 'Проведено',
  UNPOST: 'Розпроведено',
};

function changeText(row: AuditLogRow): string {
  const before = row.before_data ?? {};
  const after = row.after_data ?? {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  // Виключаємо технічні поля (id, document_number дублюється).
  keys.delete('id');
  const items: string[] = [];
  for (const k of keys) {
    const b = before[k];
    const a = after[k];
    if (k === 'document_number') {
      // Просто показуємо як контекст, не як diff.
      continue;
    }
    if (row.action === 'INSERT') {
      items.push(`${k} = ${JSON.stringify(a)}`);
    } else if (row.action === 'DELETE') {
      items.push(`${k} = ${JSON.stringify(b)}`);
    } else {
      items.push(`${k}: ${JSON.stringify(b)} → ${JSON.stringify(a)}`);
    }
  }
  return items.join('; ');
}

export default function AuditLogPage() {
  const [entityFilter, setEntityFilter] = useState('');

  const list = useQuery({
    queryKey: ['audit-log', entityFilter],
    queryFn: () => auditLog.list({
      entity_type: entityFilter || undefined,
      limit: 500,
    }),
  });

  const cols = useMemo<ColumnDef<AuditLogRow>[]>(() => [
    {
      key: 'occurred_at', header: 'Дата/час', width: 170,
      sortValue: (r) => r.occurred_at ?? '',
      cell: (r) => r.occurred_at ? new Date(r.occurred_at).toLocaleString('uk-UA') : '',
    },
    {
      key: 'user_email', header: 'Користувач', width: 130,
      sortValue: (r) => r.user_email ?? '',
      cell: (r) => r.user_email ?? '—',
    },
    {
      key: 'action', header: 'Дія', width: 130,
      sortValue: (r) => ACTION_LABEL[r.action] ?? r.action,
      cell: (r) => ACTION_LABEL[r.action] ?? r.action,
    },
    {
      key: 'entity_type', header: 'Об\'єкт', width: 140,
      sortValue: (r) => ENTITY_LABEL[r.entity_type] ?? r.entity_type,
      cell: (r) => ENTITY_LABEL[r.entity_type] ?? r.entity_type,
    },
    {
      key: 'context', header: 'Контекст', width: 200,
      cell: (r) => {
        const docNum = (r.after_data?.document_number ?? r.before_data?.document_number);
        return docNum
          ? <span style={{ color: '#3b82f6' }}>{String(docNum)}</span>
          : <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{r.entity_id.slice(0, 8)}…</span>;
      },
    },
    {
      key: 'change', header: 'Що змінилось',
      cell: (r) => <span style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>{changeText(r)}</span>,
    },
  ], []);

  function exportCsv() {
    const params = new URLSearchParams();
    if (entityFilter) params.set('entity_type', entityFilter);
    const url = `/api/v1/audit-log.csv${params.toString() ? '?' + params.toString() : ''}`;
    const token = localStorage.getItem('auth.access') ?? '';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  }

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Журнал аудиту</h2>
        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Усі об'єкти</option>
          <option value="acc.accounts">Рахунки</option>
          <option value="doc.goods_sales">Реалізації</option>
        </select>
        <button onClick={exportCsv} style={{ marginLeft: 'auto' }}>📥 Експорт CSV</button>
      </div>

      <DataTable<AuditLogRow>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => `${r.occurred_at}|${r.entity_type}|${r.entity_id}|${r.action}`}
        loading={list.isLoading}
        emptyText="Немає записів."
        initialSortKey="occurred_at"
        initialSortDir="desc"
      />

      <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
        Журнал показує критичні зміни, які здійснили користувачі:
        редагування рахунків плану рахунків (включно з прапором «не переоцінювати»)
        та зміни прапора «довідвантаження за згодою» в реалізаціях.
        Системні автоматичні записи (повні знімки таблиць від тригерів)
        приховані.
      </p>
    </>
  );
}
